import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

import { updateCard } from "../../../../../../../services/apiCards";
import { useResponsive } from "../../../../../../../hooks/useResponsive";
import useMutate from "../../../../../../../hooks/useMutate";

const schema = yup.object().shape({
  title: yup
    .string()
    .max(255, "Character limit exceeded! Maximum 255 characters allowed."),
});

export function useDetailTitleState({
  boardId,
  cardId,
  hasEditCardPermission,
  title,
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isMobile = useResponsive("down", "md");
  const formMethods = useForm({
    defaultValues: {
      title,
    },
    resolver: yupResolver(schema),
  });
  const { handleSubmit, setValue } = formMethods;

  useEffect(() => {
    setValue("title", title);
  }, [setValue, title]);

  const { mutate } = useMutate(
    updateCard,
    ["mineCards", "watching"],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: ["card", String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["cards", boardId],
      });
      setLoading(false);
    },
    () => {
      setLoading(false);
    }
  );

  const detailTitle = useMemo(
    () => (title ? title.charAt(0).toUpperCase() + title.slice(1) : ""),
    [title]
  );

  const submit = useCallback(
    (data) => {
      setOpen(false);
      if (data.title === title) {
        return;
      }

      setLoading(true);
      mutate({
        card_id: cardId,
        data,
      });
    },
    [cardId, mutate, title]
  );

  const openField = useCallback(() => {
    if (hasEditCardPermission) {
      setOpen(true);
    }
  }, [hasEditCardPermission]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSubmit(submit)();
      }
    },
    [handleSubmit, submit]
  );

  return {
    detailTitle,
    formMethods,
    handleKeyDown,
    handleSubmitTitle: handleSubmit(submit),
    isMobile,
    loading,
    open,
    openField,
  };
}
